import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { Modal, useToast, useConfirm, CountrySelect, Select, MultiSelect } from '@/components/ui';
import { companiesApi } from '@/lib/companies';
import {
  AssignmentCondition,
  AssignmentField,
  AssignmentOp,
  AssignmentRule,
  assignmentApi,
} from '@/lib/assignmentApi';
import { useCurrentUser } from '@/hooks/useAuth';

/* ────────────────────────────────────────────────────────────
 * Form types and helpers
 * ──────────────────────────────────────────────────────────── */

const FIELDS: AssignmentField[] = [
  'country_code',
  'industry',
  'employee_band',
  'jp_corp_number',
  'domain',
];

const FIELD_FALLBACKS: Record<AssignmentField, string> = {
  country_code: 'Country',
  industry: 'Industry',
  employee_band: 'Employee band',
  jp_corp_number: 'JP corporate number',
  domain: 'Domain',
};

const OPS: AssignmentOp[] = ['eq', 'contains', 'starts_with', 'in'];

const OP_FALLBACKS: Record<AssignmentOp, string> = {
  eq: 'equals',
  contains: 'contains',
  starts_with: 'starts with',
  in: 'in (CSV)',
};

type TargetKind = 'user' | 'round_robin';

interface FormState {
  name: string;
  conditions: AssignmentCondition[];
  targetKind: TargetKind;
  assignToUserId: string;
  roundRobinPool: string[];
  priority: number;
  enabled: boolean;
}

const emptyForm = (): FormState => ({
  name: '',
  conditions: [{ field: 'country_code', op: 'eq', value: 'JP' }],
  targetKind: 'user',
  assignToUserId: '',
  roundRobinPool: [],
  priority: 100,
  enabled: true,
});

/** Build the payload — picker already supplies clean UUIDs. */
function payloadFromForm(f: FormState): Parameters<typeof assignmentApi.create>[1] {
  const pool = f.roundRobinPool;
  return {
    name: f.name.trim(),
    conditions: f.conditions.map((c) => {
      if (c.op === 'in') {
        // Allow comma input for "in" — split into array.
        const arr = Array.isArray(c.value)
          ? c.value
          : String(c.value)
              .split(/[\s,]+/)
              .map((s) => s.trim())
              .filter(Boolean);
        return { ...c, value: arr };
      }
      return c;
    }),
    assignToUserId: f.targetKind === 'user' && f.assignToUserId ? f.assignToUserId : undefined,
    roundRobinPool: f.targetKind === 'round_robin' ? pool : undefined,
    priority: f.priority,
    enabled: f.enabled,
  };
}

function conditionSummary(c: AssignmentCondition): string {
  const v = Array.isArray(c.value) ? `[${c.value.join(', ')}]` : c.value;
  return `${c.field} ${c.op} ${v}`;
}

/* ────────────────────────────────────────────────────────────
 * Page
 * ──────────────────────────────────────────────────────────── */

export function AssignmentRulesPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const me = useCurrentUser();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const list = useQuery({
    queryKey: ['assignment-rules', wsId],
    queryFn: () => assignmentApi.list(wsId),
    enabled: !!wsId,
  });

  const create = useMutation({
    mutationFn: () => assignmentApi.create(wsId, payloadFromForm(form)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignment-rules', wsId] });
      setCreateOpen(false);
      setForm(emptyForm());
      toast.success(t('assignment.created'));
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(msg || t('assignment.create_failed'));
    },
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      assignmentApi.update(wsId, id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assignment-rules', wsId] }),
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => assignmentApi.remove(wsId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignment-rules', wsId] });
      toast.success(t('assignment.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const backfill = useMutation({
    mutationFn: () => assignmentApi.applyAll(wsId),
    onSuccess: (r) => {
      toast.success(
        t('assignment.backfill_done', { assigned: r.assigned, skipped: r.skipped }),
      );
    },
    onError: () => toast.error(t('assignment.backfill_failed')),
  });

  const askDelete = async (r: AssignmentRule) => {
    const ok = await confirm({
      title: t('assignment.confirm_delete_title'),
      description: t('assignment.confirm_delete_body', { name: r.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(r.id);
  };

  const items = list.data ?? [];
  const isAdmin =
    me.data?.role === 'owner' || me.data?.role === 'admin';

  // Fetch the company member list at page level so we can resolve
  // assignToUserId / roundRobinPool[] ids into display names for the
  // rule cards.
  const companyId = me.data?.companyId;
  const memberDir = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => (companyId ? companiesApi.members(companyId) : Promise.resolve([])),
    enabled: !!companyId,
  });
  const memberById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of memberDir.data ?? []) m.set(u.id, u.displayName || u.email);
    return m;
  }, [memberDir.data]);

  const targetSummary = useMemo(
    () => (r: AssignmentRule) => {
      if (r.assignToUserId) {
        const name = memberById.get(r.assignToUserId) ?? r.assignToUserId.slice(0, 8);
        return t('assignment.target_user', { id: name });
      }
      return t('assignment.target_pool', { n: r.roundRobinPool.length });
    },
    [t, memberById],
  );

  if (!isAdmin) {
    return (
      <AppShell titleKey="assignment.title">
        <p className="text-sm text-ink-600 max-w-2xl">
          {t('assignment.gated_to_admin')}
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell titleKey="assignment.title">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3 max-w-5xl">
        <p className="text-sm text-ink-600 max-w-2xl">{t('assignment.intro')}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60"
          >
            {backfill.isPending
              ? t('assignment.backfill_running')
              : t('assignment.backfill_button')}
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(emptyForm());
              setCreateOpen(true);
            }}
            className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
          >
            {t('assignment.new_button')}
          </button>
        </div>
      </div>

      {list.isLoading && (
        <p className="text-sm text-ink-500">{t('common.loading')}</p>
      )}
      {!list.isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('assignment.empty_title')}</p>
          <p className="text-xs mt-2">{t('assignment.empty_sub')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto max-w-5xl">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('assignment.col_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('assignment.col_conditions')}</th>
                <th className="py-2.5 px-3 font-medium">{t('assignment.col_target')}</th>
                <th className="py-2.5 px-3 font-medium">{t('assignment.col_priority')}</th>
                <th className="py-2.5 px-3 font-medium">{t('assignment.col_enabled')}</th>
                <th className="py-2.5 px-3 font-medium">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 text-ink-900 font-medium">{r.name}</td>
                  <td className="py-2.5 px-3 text-ink-600">
                    <div className="flex flex-wrap gap-1">
                      {r.conditions.map((c, i) => (
                        <span
                          key={i}
                          className="text-[10px] bg-ink-100 text-ink-700 rounded-md px-2 py-0.5"
                        >
                          {conditionSummary(c)}
                        </span>
                      ))}
                      {r.conditions.length === 0 && (
                        <span className="text-[10px] text-ink-400">
                          {t('assignment.match_all')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {targetSummary(r)}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">{r.priority}</td>
                  <td className="py-2.5 px-3">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={r.enabled}
                        onChange={(e) =>
                          toggleEnabled.mutate({ id: r.id, enabled: e.target.checked })
                        }
                      />
                      <span className="text-xs text-ink-500">
                        {r.enabled ? t('common.yes') : t('common.no')}
                      </span>
                    </label>
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      type="button"
                      onClick={() => askDelete(r)}
                      className="text-xs text-warn-600 hover:underline"
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('assignment.new_modal_title')}
        size="lg"
      >
        <RuleForm form={form} setForm={setForm} />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setCreateOpen(false)}
            className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={create.isPending || !form.name.trim()}
            onClick={() => create.mutate()}
            className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-60"
          >
            {create.isPending ? t('common.saving') : t('common.create')}
          </button>
        </div>
      </Modal>
    </AppShell>
  );
}

/* ────────────────────────────────────────────────────────────
 * Form sub-component
 * ──────────────────────────────────────────────────────────── */

function RuleForm({
  form,
  setForm,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
}) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const companyId = me.data?.companyId;

  const members = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => (companyId ? companiesApi.members(companyId) : Promise.resolve([])),
    enabled: !!companyId,
  });

  const memberOptions = useMemo(
    () =>
      (members.data ?? []).map((m) => ({
        value: m.id,
        label: m.displayName || m.email,
        hint: m.email,
        prefix: (
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink-200 text-[10px] font-semibold uppercase text-ink-600">
            {(m.displayName || m.email).trim().slice(0, 1)}
          </span>
        ),
        searchAliases: [m.email, m.role],
      })),
    [members.data],
  );

  const updateCondition = (i: number, patch: Partial<AssignmentCondition>) => {
    const next = [...form.conditions];
    next[i] = { ...next[i]!, ...patch } as AssignmentCondition;
    setForm({ ...form, conditions: next });
  };
  const addCondition = () =>
    setForm({
      ...form,
      conditions: [
        ...form.conditions,
        { field: 'country_code', op: 'eq', value: '' },
      ],
    });
  const removeCondition = (i: number) =>
    setForm({
      ...form,
      conditions: form.conditions.filter((_, idx) => idx !== i),
    });

  return (
    <div className="space-y-4 text-sm">
      <div>
        <label className="text-xs text-ink-500">{t('assignment.form_name')}</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5"
          placeholder={t('assignment.form_name_placeholder')}
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs text-ink-500">
            {t('assignment.form_conditions')}
          </label>
          <button
            type="button"
            onClick={addCondition}
            className="text-xs text-accent-600 hover:underline"
          >
            + {t('assignment.form_add_condition')}
          </button>
        </div>
        <div className="mt-1 space-y-2">
          {form.conditions.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-44 shrink-0">
                <Select
                  value={c.field}
                  onChange={(v) =>
                    updateCondition(i, { field: v as AssignmentField })
                  }
                  options={FIELDS.map((f) => ({
                    value: f,
                    label: t(`assignment.field_${f}`, FIELD_FALLBACKS[f]),
                  }))}
                />
              </div>
              <div className="w-32 shrink-0">
                <Select
                  value={c.op}
                  onChange={(v) =>
                    updateCondition(i, { op: v as AssignmentOp })
                  }
                  options={OPS.map((o) => ({
                    value: o,
                    label: t(`assignment.op_${o}`, OP_FALLBACKS[o]),
                  }))}
                />
              </div>
              {c.field === 'country_code' && c.op !== 'in' ? (
                <div className="flex-1 min-w-[10rem]">
                  <CountrySelect
                    value={String(Array.isArray(c.value) ? '' : c.value)}
                    onChange={(v) => updateCondition(i, { value: v })}
                    allowEmpty={{ value: '', label: t('common.select_placeholder') }}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  value={Array.isArray(c.value) ? c.value.join(', ') : c.value}
                  onChange={(e) => updateCondition(i, { value: e.target.value })}
                  placeholder={
                    c.op === 'in' ? t('assignment.form_value_csv') : t('assignment.form_value')
                  }
                  className="flex-1 rounded-md border border-ink-300 px-2 py-1"
                />
              )}
              <button
                type="button"
                onClick={() => removeCondition(i)}
                className="text-xs text-ink-500 hover:text-warn-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-ink-500">{t('assignment.form_target')}</label>
        <div className="mt-1 flex items-center gap-3">
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              checked={form.targetKind === 'user'}
              onChange={() => setForm({ ...form, targetKind: 'user' })}
            />
            <span>{t('assignment.form_target_user')}</span>
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              checked={form.targetKind === 'round_robin'}
              onChange={() => setForm({ ...form, targetKind: 'round_robin' })}
            />
            <span>{t('assignment.form_target_pool')}</span>
          </label>
        </div>
        {form.targetKind === 'user' ? (
          <div className="mt-2">
            <Select
              value={form.assignToUserId}
              onChange={(v) => setForm({ ...form, assignToUserId: v })}
              options={memberOptions}
              placeholder={t('assignment.form_user_placeholder')}
            />
          </div>
        ) : (
          <div className="mt-2">
            <MultiSelect
              value={form.roundRobinPool}
              onChange={(v) => setForm({ ...form, roundRobinPool: v })}
              options={memberOptions}
              placeholder={t('assignment.form_pool_placeholder_chips')}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-ink-500">{t('assignment.form_priority')}</label>
          <input
            type="number"
            min={0}
            max={32000}
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 num"
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            <span className="text-sm">{t('assignment.form_enabled')}</span>
          </label>
        </div>
      </div>
    </div>
  );
}
